import { useStore } from "@/context/StoreContext";
import { Field, ImageField, PageHead, Panel, SaveBar, useSettings } from "@/admin/ui";

const PRIMARY = [["#77384E", "Royal Berry"], ["#A9344D", "Ruby"], ["#344E82", "Sapphire"], ["#276653", "Emerald"], ["#30202D", "Deep Plum"]];
const ACCENT = [["#D2B27A", "Champagne"], ["#C9A46A", "Antique gold"], ["#B8945A", "Bronze"], ["#E9B9B8", "Rose"]];

const Swatches = ({ options, value, onChange, id }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([c, n]) => (
      <button key={c} type="button" onClick={() => onChange(c)} data-testid={`${id}-${n.replace(/\s/g, "-").toLowerCase()}`} className={`flex items-center gap-2 h-11 pl-2 pr-4 rounded-[12px] border text-[13px] font-bold ${value === c ? "border-plum ring-2 ring-plum/20" : "border-plum/15 bg-white"}`}>
        <span className="w-7 h-7 rounded-[8px]" style={{ background: c }} />{n}
      </button>
    ))}
  </div>
);

export default function Design() {
  const { products } = useStore();
  const { s, set, save, saving } = useSettings();
  if (!s) return <div className="h-64 rounded-[22px] bg-white animate-pulse" />;
  const T = (sec, k, label, props = {}) => <Field label={label}><input className="rh-input" value={s[sec][k] ?? ""} onChange={set(sec, k)} data-testid={`design-${sec}-${k}`} {...props} /></Field>;
  const A = (sec, k, label) => <Field label={label}><textarea rows={3} className="rh-input" value={s[sec][k] ?? ""} onChange={set(sec, k)} data-testid={`design-${sec}-${k}`} /></Field>;
  const Sel = (sec, k, label, opts) => <Field label={label}><select className="rh-input" value={s[sec][k]} onChange={set(sec, k)} data-testid={`design-${sec}-${k}`}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>;

  return (
    <div className="space-y-6" data-testid="admin-design">
      <PageHead title="Website Design" sub="Change images, words and colours. Press Save to update the live store." />
      <Panel title="Logo & favicon" sub="Leave empty to use the built-in RATNAHARA wordmark.">
        <div className="grid sm:grid-cols-3 gap-6">
          <ImageField label="Logo (for light backgrounds)" value={s.brand.logo_url} onChange={set("brand", "logo_url")} testId="design-logo-upload" />
          <ImageField label="Logo (for dark backgrounds)" value={s.brand.logo_light_url} onChange={set("brand", "logo_light_url")} testId="design-logo-light-upload" />
          <ImageField label="Favicon (square)" value={s.brand.favicon_url} onChange={set("brand", "favicon_url")} testId="design-favicon-upload" />
        </div>
      </Panel>
      <Panel title="Brand colours">
        <p className="text-[14px] font-bold mb-2">Main colour</p>
        <Swatches id="primary" options={PRIMARY} value={s.theme.primary} onChange={set("theme", "primary")} />
        <p className="text-[14px] font-bold mb-2 mt-5">Accent colour</p>
        <Swatches id="accent" options={ACCENT} value={s.theme.accent} onChange={set("theme", "accent")} />
      </Panel>
      <Panel title="Hero">
        <div className="grid sm:grid-cols-2 gap-6 mb-6">
          <ImageField label="Desktop image (landscape)" value={s.hero.desktop_image} onChange={set("hero", "desktop_image")} testId="design-hero-desktop-upload" />
          <ImageField label="Mobile image (portrait)" value={s.hero.mobile_image} onChange={set("hero", "mobile_image")} testId="design-hero-mobile-upload" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {T("hero", "eyebrow", "Small label")}
          {T("hero", "heading", "Heading")}
          <div className="sm:col-span-2">{A("hero", "description", "Description")}</div>
          {T("hero", "cta_primary", "Main button label")}
          {T("hero", "cta_secondary", "Second button label")}
          {Sel("hero", "align", "Text position", [["left", "Text on the left"], ["right", "Text on the right"]])}
          {Sel("hero", "height", "Section height", [["compact", "Compact"], ["tall", "Tall"], ["full", "Full screen"]])}
          {Sel("hero", "background", "Background", [["pearl", "Warm pearl"], ["blush", "Soft rose"], ["lilac", "Soft lavender"], ["plum", "Deep plum"]])}
          <Field label={`Image overlay darkness: ${s.hero.overlay}%`}><input type="range" min={0} max={70} value={s.hero.overlay} onChange={(e) => set("hero", "overlay")(Number(e.target.value))} className="w-full accent-[#77384E] h-12" data-testid="design-hero-overlay" /></Field>
        </div>
      </Panel>
      <Panel title="The Selected Three" sub="To change the order of products, use the arrows on the Products page.">
        <div className="grid sm:grid-cols-2 gap-4">{T("edit", "heading", "Heading")}{T("edit", "subheading", "Supporting line")}</div>
      </Panel>
      <Panel title="Hero product story">
        <div className="grid sm:grid-cols-2 gap-4">
          {Sel("story", "product_id", "Product shown", products.map((p) => [p.id, p.name]))}
          {T("story", "heading", "Heading")}
          <div className="sm:col-span-2">{A("story", "text", "Story text (leave empty to use the product's story)")}</div>
          {T("story", "cta", "Button label")}
          <ImageField label="Campaign image" value={s.story.image} onChange={set("story", "image")} testId="design-story-image-upload" />
          <ImageField label="Small image (corner)" value={s.story.image2} onChange={set("story", "image2")} testId="design-story-image2-upload" />
        </div>
      </Panel>
      <Panel title="Occasion finder">
        <div className="grid sm:grid-cols-2 gap-4">{T("occasion", "heading", "Heading")}{T("occasion", "subheading", "Supporting line")}</div>
        <p className="text-[13px] text-plum/60 mt-3">Which product appears for each occasion is set on each product (Products → Edit → Occasions).</p>
      </Panel>
      <Panel title="Brand section">
        <div className="grid sm:grid-cols-2 gap-4">
          {T("details", "heading", "Heading")}
          <div className="sm:col-span-2">{A("details", "text", "Text")}</div>
          <ImageField label="Large image" value={s.details.image} onChange={set("details", "image")} testId="design-details-image-upload" />
          <ImageField label="Small image" value={s.details.image2} onChange={set("details", "image2")} testId="design-details-image2-upload" />
        </div>
      </Panel>
      <Panel title="Closing section & footer">
        <div className="grid sm:grid-cols-2 gap-4">
          {T("final", "heading", "Heading")}
          {T("final", "cta", "Button label")}
          <ImageField label="Image" value={s.final.image} onChange={set("final", "image")} testId="design-final-image-upload" />
          <div className="sm:col-span-2">{A("footer", "description", "Footer description")}</div>
        </div>
      </Panel>
      <SaveBar save={save} saving={saving} />
    </div>
  );
}
